import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { clearAuthSession } from "./auth/session";

export type OfficerIdentity = {
  id: string;
  name?: string;
};

export type SessionSnapshot = {
  officer?: OfficerIdentity;
};

const STORAGE_KEYS = ["phmis.session", "phmis_session", "phmis.currentSession"];
const OFFICER_ID_KEYS = ["phmis.officerId", "phmis.officer.id", "officerId", "user.officerId"];
const OFFICER_NAME_KEYS = ["phmis.officerName", "phmis.officer.name", "officerName", "user.officerName"];
const SESSION_EVENT = "phmis:session";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "scroll", "click", "touchstart", "visibilitychange"] as const;
const INACTIVITY_TIMEOUT_MS = 3 * 60 * 1000;
const WARNING_TIMEOUT_MS = 2 * 60 * 1000;

type MaybeStorage = Pick<Storage, "getItem"> | undefined;

type AnyObject = Record<string, unknown>;

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function readOfficerFromObject(value: unknown): OfficerIdentity | null {
  if (!value || typeof value !== "object") return null;
  const obj = value as AnyObject;
  const officerLike = (obj.officer && typeof obj.officer === "object" ? obj.officer as AnyObject : obj);
  const id = ["id", "officerId"].map((key) => officerLike?.[key]).find((v): v is string => typeof v === "string" && v.length > 0)
    ?? OFFICER_ID_KEYS.map((key) => obj[key]).find((v): v is string => typeof v === "string" && v.length > 0);
  const name = ["name", "officerName"].map((key) => officerLike?.[key]).find((v): v is string => typeof v === "string" && v.length > 0)
    ?? OFFICER_NAME_KEYS.map((key) => obj[key]).find((v): v is string => typeof v === "string" && v.length > 0);
  if (!id) return null;
  return { id, name };
}

function readOfficerFromStorage(storage: MaybeStorage): OfficerIdentity | null {
  if (!storage) return null;
  for (const key of STORAGE_KEYS) {
    const parsed = safeParse<SessionSnapshot | AnyObject>(storage.getItem(key));
    const officer = readOfficerFromObject(parsed ?? undefined);
    if (officer) return officer;
  }
  const direct: AnyObject = {};
  for (const key of OFFICER_ID_KEYS) {
    const value = storage.getItem(key);
    if (value) direct[key] = value;
  }
  for (const key of OFFICER_NAME_KEYS) {
    const value = storage.getItem(key);
    if (value) direct[key] = value;
  }
  return readOfficerFromObject(direct);
}

function readGlobalOfficer(): OfficerIdentity | null {
  if (typeof window === "undefined") return null;
  const anyWindow = window as unknown as AnyObject;
  const globalCandidates = [
    anyWindow.__PHMIS_SESSION__,
    anyWindow.PHMIS_SESSION,
    anyWindow.session,
    anyWindow.currentUser,
  ];
  for (const candidate of globalCandidates) {
    const officer = readOfficerFromObject(candidate);
    if (officer) return officer;
  }
  return null;
}

export function getCurrentOfficer(): OfficerIdentity | null {
  if (typeof window === "undefined") return null;
  const storages: MaybeStorage[] = [window.sessionStorage, window.localStorage];
  for (const storage of storages) {
    const officer = readOfficerFromStorage(storage);
    if (officer) return officer;
  }
  return readGlobalOfficer();
}

const SessionContext = createContext<OfficerIdentity | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const initial = useMemo(() => getCurrentOfficer(), []);
  const [officer, setOfficer] = useState<OfficerIdentity | null>(initial);

  useEffect(() => {
    const update = () => setOfficer(getCurrentOfficer());
    window.addEventListener("storage", update);
    window.addEventListener(SESSION_EVENT, update as EventListener);
    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener(SESSION_EVENT, update as EventListener);
    };
  }, []);

  return <SessionContext.Provider value={officer}>{children}</SessionContext.Provider>;
}

type IdleWarningDialogProps = {
  open: boolean;
  onStaySignedIn: () => void;
  onSignOut: () => void;
};

function IdleWarningDialog({ open, onStaySignedIn, onSignOut }: IdleWarningDialogProps) {
  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-900/10">
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">Are you still there?</h2>
          <p className="text-sm leading-6 text-slate-600">
            You&apos;ve been inactive for a while. You&apos;ll be signed out in 1 minute unless you stay signed in.
          </p>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onSignOut}
            className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Sign out now
          </button>
          <button
            type="button"
            onClick={onStaySignedIn}
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function IdleTimeoutProvider({ children }: { children: React.ReactNode }) {
  const warningTimerRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const [warningOpen, setWarningOpen] = useState(false);
  const officer = useCurrentOfficer();

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current !== null) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleTimeout = useCallback(() => {
    clearTimers();
    setWarningOpen(false);
    clearAuthSession();
    notifySessionChange();
  }, [clearTimers]);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    warningTimerRef.current = window.setTimeout(() => setWarningOpen(true), WARNING_TIMEOUT_MS);
    timeoutRef.current = window.setTimeout(() => handleTimeout(), INACTIVITY_TIMEOUT_MS);
  }, [clearTimers, handleTimeout]);

  const recordActivity = useCallback(() => {
    setWarningOpen(false);
    scheduleTimers();
  }, [scheduleTimers]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    if (!officer) {
      clearTimers();
      setWarningOpen(false);
      return undefined;
    }

    recordActivity();

    const activityHandler = () => recordActivity();
    const sessionChangeHandler = () => recordActivity();

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, activityHandler, { passive: true });
    }
    window.addEventListener("storage", sessionChangeHandler);
    window.addEventListener(SESSION_EVENT, sessionChangeHandler as EventListener);

    return () => {
      clearTimers();
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, activityHandler);
      }
      window.removeEventListener("storage", sessionChangeHandler);
      window.removeEventListener(SESSION_EVENT, sessionChangeHandler as EventListener);
    };
  }, [clearTimers, officer, recordActivity]);

  return (
    <>
      {children}
      <IdleWarningDialog
        open={warningOpen}
        onStaySignedIn={recordActivity}
        onSignOut={handleTimeout}
      />
    </>
  );
}

export function useCurrentOfficer(): OfficerIdentity | null {
  return useContext(SessionContext);
}

export function notifySessionChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SESSION_EVENT));
}
